/**
 * tariffs/telangana.ts — TGSPDCL HT Category 1A Tariff (Telangana)
 * ==================================================================
 * Source: VIREON-CORTEX Variables & Formulas Reference PDF (Telangana)
 * Sample bills: SGR2985 / SGR2502 — January–February 2026
 *
 * Category: HT Consumer, Category 1A, 33 kV supply
 * Energy billing unit: kVAh (apparent energy — NOT kWh)
 * Demand minimum: 80% of Contract Demand
 * Three-tier rate structure based on Max Demand vs Contract Demand ratio.
 *
 * Key differences from WB tariff:
 *  - Energy charges are per kVAh (not kWh)
 *  - Three-tier rates based on MD vs CD ratio
 *  - Demand: ₹500/kVA (normal), ₹1000/kVA (penal excess)
 *  - Min demand = 80% of CD
 *  - ED = 6 paise/kVAh flat (not %)
 *  - Customer charges = ₹3500/month (flat)
 *  - No LF rebate, no PF rebate table, no meter rental, no TPR
 *
 * DO NOT hardcode these values elsewhere.
 * For tariff revisions, update ONLY this file.
 */

import type { StateTariff, ToDPeriod, PfAdjustmentRow, LfRebateRow } from './types';

// ─── ToD ──────────────────────────────────────────────────────────────────────

/**
 * TGSPDCL HT Time-of-Day schedule.
 *
 * Source of truth: TGERC Retail Supply Tariff Order for FY 2026-27
 * (Appendix A, Terms & Conditions §1), effective 01.04.2026, for HT-I(A):
 *
 *   6 am–10 am AND 6 pm–10 pm  →  Plus  Rs. 1.50/unit   (PEAK)
 *   10 am–6 pm                 →  Less  Rs. 0.50/unit   (daytime solar incentive)
 *   10 pm–6 am                 →  normal energy charge  (no adjustment)
 *
 * WHY the incentive is DAYTIME, not night: the FY 2025-26 order withdrew the old
 * night (10pm–6am) rebate, and the FY 2026-27 order moved the incentive to the
 * 10am–6pm solar-surplus window. So the −0.50 rebate lands in the middle of the
 * day, and the night block reverts to the plain base rate. This matches the
 * SGR2985 invoice exactly: "TOD Charges" (peak) = 156,810 kVAh × 1.50, and the
 * two "Day Incentive" register lines = 190,170 kVAh × 0.50.
 *
 * The three RATES_* tiers below already encode these adders (PEAK = base+1.50,
 * OFF-PEAK = base−0.50), so OFF-PEAK is the DAYTIME incentive bucket here — it is
 * not a night window. Each entry is a half-open [startHour, endHour) in meter
 * (IST) local time; together the five slots cover all 24 hours.
 */
const TOD_SLOTS: { period: ToDPeriod; startHour: number; endHour: number }[] = [
  { period: 'PEAK',     startHour: 6,  endHour: 10 },  // morning peak   (+1.50)
  { period: 'PEAK',     startHour: 18, endHour: 22 },  // evening peak   (+1.50)
  { period: 'OFF-PEAK', startHour: 10, endHour: 18 },  // daytime solar incentive (−0.50)
  { period: 'NORMAL',   startHour: 22, endHour: 24 },  // night: base rate, no adjustment
  { period: 'NORMAL',   startHour: 0,  endHour: 6  },  // night: base rate, no adjustment
];

function todPeriodFor(hour: number): ToDPeriod {
  const slot = TOD_SLOTS.find(s => hour >= s.startHour && hour < s.endHour);
  // All 24 hours are covered above; NORMAL (base rate) is the safe default.
  return slot ? slot.period : 'NORMAL';
}

// ─── Energy Rates (Rs/kVAh) — Three Tiers ────────────────────────────────────

/** Tier 1: MD kVA ≤ 1.2 × CD — standard rates */
const RATES_T1: Record<ToDPeriod, number> = {
  'NORMAL':   7.15,
  'PEAK':     8.65,
  'OFF-PEAK': 6.65,
};

/** Tier 2: 1.2×CD < MD ≤ 2×CD — 1.15× multiplier */
const RATES_T2: Record<ToDPeriod, number> = {
  'NORMAL':   8.22,
  'PEAK':     9.95,
  'OFF-PEAK': 7.65,
};

/** Tier 3: MD > 2×CD — 1.20× multiplier */
const RATES_T3: Record<ToDPeriod, number> = {
  'NORMAL':   8.58,
  'PEAK':     10.38,
  'OFF-PEAK': 7.98,
};

/**
 * Ratio of billed Max Demand to Contract Demand. Guards a zero/invalid CD
 * (returns 0 → Tier 1) so we never divide by zero.
 */
function mdCdRatio(mdKva: number, cdKva: number): number {
  return cdKva > 0 ? mdKva / cdKva : 0;
}

/**
 * Tier-aware energy rate lookup (PDF §3.1–3.3):
 *   • MD ≤ 1.2×CD          → Tier 1 (standard)
 *   • 1.2×CD < MD ≤ 2×CD   → Tier 2 (×1.15)
 *   • MD > 2×CD            → Tier 3 (×1.20)
 */
function getTariffRates(mdKva: number, cdKva: number): Record<ToDPeriod, number> {
  const ratio = mdCdRatio(mdKva, cdKva);
  if (ratio > 2.0) return RATES_T3;
  if (ratio > 1.2) return RATES_T2;
  return RATES_T1;
}

function getTierLabel(mdKva: number, cdKva: number): string {
  const ratio = mdCdRatio(mdKva, cdKva);
  if (ratio > 2.0) return 'Tier 3 (MD > 2×CD) — ×1.20';
  if (ratio > 1.2) return 'Tier 2 (1.2×CD < MD ≤ 2×CD) — ×1.15';
  return 'Tier 1 (MD ≤ 1.2×CD)';
}

// ─── PF — TGSPDCL HT does not use a PF rebate/surcharge table ────────────────
// (It uses RKVAH surcharge which is modelled separately at meter level.)

const PF_ADJUSTMENT_TABLE: PfAdjustmentRow[] = [
  { minPf: 0.95, maxPf: Infinity, normalPct: 0, peakPct: 0, offPeakPct: 0 },
  { minPf: 0.00, maxPf: 0.95,     normalPct: 0, peakPct: 0, offPeakPct: 0 },
];

function pfAdjustmentPct(_pf: number, _period: ToDPeriod): number { return 0; }

// ─── LF Rebate — TGSPDCL HT has no LF rebate ──────────────────────────────────

const LF_REBATE_TABLE: LfRebateRow[] = [];
function lfRebateRsPerKwh(_lfPct: number): number { return 0; }

// ─── Tariff Object ────────────────────────────────────────────────────────────

export const TelanganaTariff: StateTariff = {
  // Identity
  stateCode:        'telangana',
  stateName:        'Telangana',
  discName:         'TGSPDCL',
  discFullName:     'Southern Power Distribution Company of Telangana Ltd.',
  tariffCategory:   'HT Cat. 1A',
  voltageLevel:     '33 kV',

  // Energy unit: kVAh (apparent energy)
  energyUnit: 'kvah',

  // ToD
  todWindows: {
    'NORMAL':   { label: 'Normal',        range: '22:00–06:00' },
    'PEAK':     { label: 'Peak',          range: '06:00–10:00 · 18:00–22:00' },
    'OFF-PEAK': { label: 'Day Incentive', range: '10:00–18:00' },
  },
  todColors: {
    'NORMAL':   '#0FB18A',
    'PEAK':     '#F97316',
    'OFF-PEAK': '#118AB2',
  },
  todPeriodFor,

  // Energy rates (Rs/kVAh, Tier 1 as base)
  energyRates:    RATES_T1,
  getTariffRates,
  getTierLabel,

  // Demand charges
  demandChargeModel:        'normal-penal',
  demandChargeRsKva:        500,   // Rs/kVA/month — normal demand
  demandPenalRsKva:         1000,  // Rs/kVA/month — excess above CD
  minDemandPct:             0.80,  // Min chargeable = 80% of CD
  defaultContractDemandKva: 2501,

  // PF: no table-based adjustment
  hasPfAdjustment:    false,
  pfAdjustmentTable:  PF_ADJUSTMENT_TABLE,
  pfAdjustmentPct,
  pfPenaltyThreshold: 0.85,
  pfRebateThreshold:  0.95,
  pfTarget:           0.95,
  pfSustainedFloor:   0.90,

  // LF: no rebate
  hasLfRebate:      false,
  lfRebateTable:    LF_REBATE_TABLE,
  lfRebateRsPerKwh,

  // Electricity duty: flat Rs per kVAh
  electricityDutyModel: 'per-kvah',
  electricityDutyValue: 0.06,      // 6 paise/kVAh

  // Fixed charges
  meterRentalRsMonth:     0,
  customerChargesRsMonth: 3500,
  // Deposit-linked surcharge — supplied per cycle from the DISCOM deposit record
  // (the June invoice carried Rs 48,736). 0 here so it never silently fabricates
  // a charge; override via the tariff/source when the real figure is known.
  acdSurchargeRs:         0,
  tprFraction:            0,       // No TPR
  lpseFraction:           0.0125,  // 1.25% LPSC if paid late

  // Carbon
  carbonKgPerKwh: 0.82,

  // Sensor quality
  voltageDropoutV:    100,
  outlierPercentile:  99.5,
  maxIntegrationGapH: 1,

  // Off-peak demand ceiling: not applicable for TGSPDCL HT model
  offPeakMaxDemandPct: 0,
};
